/* eslint-disable graphql/template-strings */
import * as React from 'react';
import { graphql } from 'react-apollo';

import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { get, keyBy, keys, map } from 'lodash';
import { compose, mapProps } from 'recompose';

import waitForGraphQL from 'js/lib/waitForGraphQL';

import { Box } from '@coursera/coursera-ui';

import SlackAccountComponent from 'bundles/slack-account-link/components/SlackAccountComponent';
import type { SlackAccount } from 'bundles/slack-account-link/types/SlackAccount';
import SvgSlack from 'bundles/third-party-auth/components/SvgSlack';
import SlackDegreesQuery from 'bundles/third-party-auth/queries/SlackDegreesQuery.graphql';
import type {
  SlackDegreesQuery as SlackDegreesData,
  SlackDegreesQueryVariables,
} from 'bundles/third-party-auth/queries/__generated__/SlackDegreesQuery';

import _t from 'i18n!nls/third-party-auth';

import 'css!./__styles__/AccountLinkSlack';

export type SlackAccountData = SlackAccount & {
  degreeName: string;
  partnerName: string;
  slackTeamDomainName?: string;
};

type Partner = {
  id: string;
  name: string;
};

type Degree = {
  id: string;
  name: string;
  partners: Partner[];
};

type ExternalSlackTeamInformation = {
  id: string;
  degreeId: string;
  slackTeam: SlackTeam;
};

type SlackTeam = {
  slackTeamDomain: string;
  slackTeamName: string;
};

type Props = {
  accounts?: SlackAccountData[];
};

type IntermediateProps1 = Props & {
  degreeIds: string[];
};

type IntermediateProps2 = IntermediateProps1 & {
  accounts: SlackAccount[];
  degrees: Record<string, Degree>;
  slackTeams: Record<string, ExternalSlackTeamInformation>;
};

const useDegreesData = (degreeIds: string[]) => {
  const { data } = useQuery<SlackDegreesData, SlackDegreesQueryVariables>(SlackDegreesQuery, {
    variables: {
      ids: degreeIds,
    },
    errorPolicy: 'all',
    context: {
      clientName: 'gatewayGql',
    },
  });

  return data?.Degree?.queryByIds;
};

export class AccountLinkSlack extends React.Component<Props> {
  render() {
    // Some degrees/mastertrack aren't configured a slack workspace hence filter
    // out the account entries that do not have a slackTeamDomainName configured
    const accounts = keyBy(
      this.props.accounts?.filter((account) => account.slackTeamDomainName),
      'id'
    );

    if (!accounts || keys(accounts).length === 0) {
      return null;
    }

    return (
      <div className="rc-LinkSlack account-link-option">
        <Box alignItems="end" justifyContent="start" style={{ marginTop: '12px', marginLeft: '-0.5rem' }}>
          <h5 className="account-link-option__header">
            <SvgSlack size={20} />
            {_t('Slack')}
          </h5>
        </Box>

        <div className="slack-account-list">
          {map(accounts, ({ id, degreeName, degreeId, partnerName, email, status, slackTeamDomainName }) => {
            return (
              <div key={id} id={id} className="slack-account-item" style={{ marginTop: '1rem' }}>
                <Box alignContent="start" flexDirection="column">
                  <div className="slack-account-program-name-header" style={{ fontWeight: 'bold', fontSize: '14px' }}>
                    {degreeName}
                  </div>
                  <div
                    className="slack-account-partner-name-header"
                    style={{ fontWeight: 'normal', fontSize: '12px', marginBottom: '10px' }}
                  >
                    {partnerName}
                  </div>
                </Box>

                <SlackAccountComponent
                  key={id}
                  id={id}
                  degreeId={degreeId}
                  email={email}
                  status={status}
                  slackTeamDomainName={slackTeamDomainName}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

const slackAccountDataProviderHoc = waitForGraphQL(
  gql`
    query SlackMyAccountQuery {
      SlackAuthV1Resource(input: {})
        @rest(type: "SlackAuthV1Resource", path: "slackAuth.v1?q=me&fields=email,status", method: "GET") {
        elements @type(name: "SlackAccount") {
          id
          email
          status
          degreeId
        }
      }
    }
  `,
  {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    props: ({ data: { SlackAuthV1Resource } }: any) => {
      return {
        accounts: SlackAuthV1Resource?.elements,
        degreeIds: SlackAuthV1Resource?.elements
          ? SlackAuthV1Resource?.elements.map((account: SlackAccount) => account.degreeId)
          : [],
      };
    },
  }
);

const externalSlackDataProviderHOC = graphql(
  gql`
    query DegreesQuery($ids: String!) {
      ExternalSlackTeamInformationV1Resource(ids: $ids)
        @rest(
          type: "ExternalSlackTeamInformationV1Resource"
          path: "externalSlackTeamInformation.v1?q=byDegreeIds&ids={args.ids}&fields=slackTeam,degreeId"
          method: "GET"
        ) {
        elements @type(name: "ExternalSlackTeamInformation") {
          id
          slackTeam
          degreeId
        }
      }
    }
  `,
  {
    options: ({ accounts }: { accounts: Array<SlackAccount> }) => {
      const degreeIds = accounts ? accounts.map((account: SlackAccount) => account.degreeId) : [];
      return { variables: { ids: degreeIds.join(',').toString() } };
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    props: ({ data: { ExternalSlackTeamInformationV1Resource } }: any) => {
      return { slackTeams: ExternalSlackTeamInformationV1Resource?.elements || [] };
    },
  }
);

export const transformData = (
  accounts: SlackAccount[],
  degrees: Record<string, Degree>,
  slackTeams: Record<string, ExternalSlackTeamInformation>
) => {
  const data = map(accounts, (account) => {
    const degree = get(degrees, account.degreeId);
    const slackTeamDomain = get(slackTeams, account.degreeId);
    return {
      ...account,
      degreeName: degree?.name || '',
      partnerName: degree?.partners[0]?.name || '',
      slackTeamDomainName: slackTeamDomain?.slackTeam.slackTeamDomain,
    };
  });
  return data;
};

export default compose<Props, {}>(
  slackAccountDataProviderHoc,
  mapProps((props: IntermediateProps1) => ({
    ...props,
    degrees: useDegreesData(props.degreeIds),
  })),
  externalSlackDataProviderHOC,
  mapProps<Props, IntermediateProps2>(({ accounts, degrees, slackTeams }) => {
    const degreesDataForDegreeId = keyBy(degrees, 'id');
    const slackTeamsForDegreeId = keyBy(slackTeams, 'degreeId');
    return { accounts: transformData(accounts, degreesDataForDegreeId, slackTeamsForDegreeId) };
  })
)(AccountLinkSlack);
